/**
 * GelatoMaps — Minimal Store with pub/sub
 * Single source of truth for shared frontend state.
 * @module store
 */

/** @typedef {import('./types').Shop} Shop */
/** @typedef {import('./types').Filters} Filters */

const _state = {
  /** @type {Shop[]} */
  shops: [],
  /** @type {Array<{marker: any, item: Shop}>} */
  markers: [],
  /** @type {Filters} */
  filters: {
    q: '', ciudad: '', cats: new Set(), bolasF: new Set(),
    rMin: 0, web: false, tel: false, tmp: true,
    maxDist: 0, minReviews: 0, minBolas: 0, soloArtesanal: false,
  },
  /** @type {number} */
  selectedShopIdx: -1,
  /** @type {number} */
  focusedMarkerIdx: -1,
  /** @type {Object} */
  premium: {},
  /** @type {Object} */
  featureFlags: { monetization_visible: false, premium_enabled: false },
  /** @type {{ token: string|null, user: object|null, isAdmin: boolean }} */
  auth: { token: null, user: null, isAdmin: false },
  /** @type {{ sidebarOpen: boolean, panelOpen: boolean, modalId: string|null }} */
  ui: { sidebarOpen: false, panelOpen: false, modalId: null },
  /** @type {{ lat: number|null, lng: number|null, active: boolean }} */
  geo: { lat: null, lng: null, active: false },
}

/** @type {Map<string, Set<Function>>} */
const _subs = new Map()

export const store = {
  /**
   * Get state by key.
   * @param {string} key
   * @returns {any}
   */
  get(key) {
    return _state[key]
  },

  /**
   * Set state and notify subscribers.
   * @param {string} key
   * @param {any} value
   */
  set(key, value) {
    const old = _state[key]
    _state[key] = value
    const subs = _subs.get(key)
    if (subs) subs.forEach(fn => {
      try { fn(value, old) } catch (e) { console.error(`[store] subscriber error on '${key}':`, e) }
    })
  },

  /**
   * Subscribe to state changes.
   * @param {string} key
   * @param {Function} fn
   * @returns {Function} unsubscribe function
   */
  on(key, fn) {
    if (!_subs.has(key)) _subs.set(key, new Set())
    _subs.get(key).add(fn)
    return () => _subs.get(key).delete(fn)
  },

  /**
   * Get full state snapshot (for debugging).
   * @returns {Object}
   */
  snapshot() {
    return { ..._state }
  },
}

// Expose for debugging in dev console
if (typeof window !== 'undefined') {
  window.__store = store
}
